import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import {
  useFetchNotificationsQuery,
  useLazyFetchNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "../../api/notificationsApi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { formatMoney } from "../../views/salesUtils";
import { useAuth } from "../../views/AuthContext";
import {
  formatNotificationDateTime,
  getNotificationNavigation,
} from "./notificationPresentation";

export default function NotificationBell() {
  const t = useTranslation();
  const { language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = React.useState(false);
  const [openingId, setOpeningId] = React.useState(null);
  const [filter, setFilter] = React.useState("all");
  const [extraItems, setExtraItems] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [knownUnreadCount, setKnownUnreadCount] = React.useState(0);
  const filterRef = React.useRef(filter);
  const tokenRef = React.useRef(token);
  const queryArgs = React.useMemo(() => ({ filter }), [filter]);

  const {
    currentData: data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useFetchNotificationsQuery(queryArgs, {
    skip: !token,
    pollingInterval: 60_000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  });
  const [loadMore, { isFetching: isLoadingMore, isError: isLoadMoreError }] =
    useLazyFetchNotificationsQuery();
  const [markRead, { isError: isReadError }] = useMarkNotificationReadMutation();
  const [markAllRead, { isLoading: isMarkingAll, isError: isMarkAllError }] =
    useMarkAllNotificationsReadMutation();

  React.useEffect(() => {
    filterRef.current = filter;
    setExtraItems([]);
    setNextCursor(null);
  }, [filter]);

  React.useEffect(() => {
    tokenRef.current = token;
    setExtraItems([]);
    setNextCursor(null);
    setKnownUnreadCount(0);
    setOpeningId(null);
  }, [token]);

  React.useEffect(() => {
    if (!data || extraItems.length > 0) return;
    setNextCursor(data.nextCursor || null);
  }, [data, extraItems.length]);

  React.useEffect(() => {
    if (data) setKnownUnreadCount(Number(data.unreadCount || 0));
  }, [data]);

  React.useEffect(() => {
    if (!token) setKnownUnreadCount(0);
  }, [token]);

  const toggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen && !isFetching) {
      refetch();
    }
  };

  const openNotification = async (notification) => {
    if (openingId) return;
    const requestedToken = token;
    setOpeningId(notification.id);
    try {
      if (!notification.readAt) {
        try {
          const { readAt } = await markRead({ notificationId: notification.id, filter }).unwrap();
          // The RTK cache patch in notificationsApi only covers the first
          // (cursor-less) page. A notification loaded via "Load more" lives in
          // this local extraItems state instead, so it needs its own update or
          // it keeps rendering as unread after being marked read.
          setExtraItems((current) => {
            if (!current.some((item) => item.id === notification.id)) return current;
            return current.map((item) =>
              item.id === notification.id ? { ...item, readAt } : item
            );
          });
        } catch {
          // Read state is ancillary to the destination. Leave it unread and let
          // the mutation error remain available when the menu is opened again.
        }
      }
      if (tokenRef.current !== requestedToken) return;
      setIsOpen(false);
      const destination = getNotificationNavigation(notification);
      navigate(destination.path, { state: destination.state });
    } finally {
      setOpeningId(null);
    }
  };

  const handleFilterChange = (nextFilter) => {
    if (nextFilter !== filter) setFilter(nextFilter);
  };

  const handleLoadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    const requestedFilter = filter;
    const requestedToken = token;
    try {
      const page = await loadMore({ filter: requestedFilter, cursor: nextCursor }).unwrap();
      if (filterRef.current !== requestedFilter || tokenRef.current !== requestedToken) return;
      setExtraItems((current) => {
        const knownIds = new Set(current.map((item) => item.id));
        return [...current, ...(page.items || []).filter((item) => !knownIds.has(item.id))];
      });
      setNextCursor(page.nextCursor || null);
    } catch {
      // The translated retry state remains visible below the current page.
    }
  };

  const handleMarkAllRead = async () => {
    if (unreadCount === 0 || isMarkingAll) return;
    const requestedFilter = filter;
    const requestedToken = token;
    try {
      const result = await markAllRead({ filter: requestedFilter }).unwrap();
      if (tokenRef.current !== requestedToken) return;
      setKnownUnreadCount(Number(result.unreadCount || 0));
      if (filterRef.current !== requestedFilter) return;
      setExtraItems([]);
      setNextCursor(null);
      await refetch();
    } catch {
      // No optimistic list changes are made; the previous state stays accurate.
    }
  };

  const unreadCount = data ? Number(data.unreadCount || 0) : knownUnreadCount;
  const items = React.useMemo(() => {
    const seen = new Set();
    return [...(data?.items || []), ...extraItems].filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [data?.items, extraItems]);
  const showInitialLoading = !data && (isLoading || isFetching);

  return (
    <Dropdown nav isOpen={isOpen} toggle={toggle} className="cms-notifications">
      <DropdownToggle
        caret
        color="default"
        nav
        aria-label={
          unreadCount
            ? `${t("navbar.notifications")}: ${unreadCount}`
            : t("navbar.notifications")
        }
        aria-expanded={isOpen}
      >
        <span className="cms-notifications__icon-wrap">
          <i className="tim-icons icon-bell-55" aria-hidden="true" />
          {unreadCount > 0 ? (
            <span className="cms-notifications__badge" aria-hidden="true">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </span>
        <p className="d-lg-none">{t("navbar.notifications")}</p>
      </DropdownToggle>

      <DropdownMenu className="cms-notifications__menu" right tag="div">
        <div className="cms-notifications__header">
          <strong>{t("navbar.notifications")}</strong>
          <div className="cms-notifications__header-actions">
            <button
              type="button"
              className="cms-notifications__mark-all"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0 || isMarkingAll}
            >
              {isMarkingAll ? t("navbar.markingAllRead") : t("navbar.markAllRead")}
            </button>
            {isFetching && !showInitialLoading ? (
              <span className="cms-notifications__refresh" aria-label={t("navbar.refreshing")} />
            ) : null}
          </div>
        </div>

        <div className="cms-notifications__filters" aria-label={t("navbar.notificationFilters")}>
          {[
            ["all", "navbar.allNotifications"],
            ["unread", "navbar.unreadNotifications"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={filter === value ? "is-active" : ""}
              aria-pressed={filter === value}
              onClick={() => handleFilterChange(value)}
            >
              {t(label)}
            </button>
          ))}
        </div>

        {showInitialLoading ? (
          <div className="cms-notifications__state" role="status">
            {t("navbar.notificationsLoading")}
          </div>
        ) : isError ? (
          <div className="cms-notifications__state cms-notifications__state--error" role="alert">
            <span>{t("navbar.notificationsError")}</span>
            <button type="button" onClick={() => refetch()}>
              {t("messages.retry")}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="cms-notifications__state">
            {t(filter === "unread" ? "navbar.noUnreadNotifications" : "navbar.noNotifications")}
          </div>
        ) : (
          <div className="cms-notifications__list" aria-label={t("navbar.notifications")}>
            {items.map((notification) => {
              const unread = !notification.readAt;
              const isStockNotification =
                notification.type === "PRODUCT_VARIANT_OUT_OF_STOCK";
              const variant = notification.variant || {};
              return (
                <DropdownItem
                  key={notification.id}
                  tag="button"
                  type="button"
                  className={`cms-notifications__item${unread ? " is-unread" : ""}`}
                  onClick={() => openNotification(notification)}
                  disabled={openingId === notification.id}
                >
                  <span className="cms-notifications__dot" aria-hidden="true" />
                  <span className="cms-notifications__content">
                    <span className="cms-notifications__title">
                      {t(isStockNotification ? "navbar.variantOutOfStock" : "navbar.newOrder")}
                    </span>
                    <span className="cms-notifications__order">
                      {isStockNotification
                        ? [variant.productTitle, variant.optionSummary || variant.variantTitle]
                            .filter(Boolean)
                            .join(" · ")
                        : notification.order.orderNumber}
                    </span>
                    <span className="cms-notifications__meta">
                      {isStockNotification ? null : (
                        <>
                          {formatMoney(
                            notification.order.totalAmount,
                            notification.order.currencyCode,
                          )}
                          <span aria-hidden="true"> · </span>
                        </>
                      )}
                      {formatNotificationDateTime(
                        isStockNotification ? notification.createdAt : notification.order.createdAt,
                        language,
                      )}
                    </span>
                    {unread ? (
                      <span className="sr-only">{t("navbar.unreadNotification")}</span>
                    ) : null}
                  </span>
                </DropdownItem>
              );
            })}
            {nextCursor ? (
              <button
                type="button"
                className="cms-notifications__load-more"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t("navbar.loadingMore") : t("navbar.loadMore")}
              </button>
            ) : null}
            {isLoadMoreError ? (
              <div className="cms-notifications__page-error" role="alert">
                <span>{t("navbar.loadMoreError")}</span>
                <button type="button" onClick={handleLoadMore}>
                  {t("messages.retry")}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {isReadError ? (
          <div className="cms-notifications__read-error" role="alert">
            {t("navbar.notificationReadError")}
          </div>
        ) : null}
        {isMarkAllError ? (
          <div className="cms-notifications__read-error" role="alert">
            {t("navbar.markAllReadError")}
          </div>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
}
